import {
    BriefcaseBusinessIcon,
    CreditCardIcon,
    FileTextIcon,
    LayoutDashboardIcon,
    LogOutIcon,
    MaximizeIcon,
    MinimizeIcon,
    SettingsIcon,
    UserCircleIcon,
} from "lucide-react";

import {Avatar, AvatarFallback} from "@components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@components/ui/select";
import {useChatContext} from "@contexts/ChatContext";
import {useIntegrations} from "@hooks/use-integrations";
import {useUser} from "@hooks/use-user";

interface TopBarProps {
    topTab: "dashboard" | "financial" | "alseasons";
    onTabChange: (tab: "dashboard" | "financial" | "alseasons") => void;
    showAlseasons?: boolean;
}

export function TopBar({topTab, onTabChange, showAlseasons = false}: TopBarProps) {
    const {contentFullscreen, setContentFullscreen} = useChatContext();
    const {user} = useUser();
    const {getIntegration, setXeroTenant} = useIntegrations();
    const xeroIntegration = getIntegration("xero");

    const displayName = user
        ? (user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`.trim()
            : user.firstName || user.lastName || "User")
        : "User";
    const firstInitial = user?.firstName?.[0] ?? "";
    const lastInitial = user?.lastName?.[0] ?? "";
    const avatarName = (firstInitial + lastInitial).toUpperCase() || "P";

    const tabs: {key: "dashboard" | "financial" | "alseasons"; label: string; icon: typeof LayoutDashboardIcon}[] = [
        {key: "dashboard", label: "Dashboard", icon: LayoutDashboardIcon},
        {key: "financial", label: "Financial Model", icon: FileTextIcon},
        ...(showAlseasons ? [{key: "alseasons" as const, label: "Alseasons", icon: BriefcaseBusinessIcon}] : []),
    ];

    return (
        <div className="flex items-center h-[46px] bg-white border-b border-[#E2E8F0] px-5 flex-shrink-0">
            {/* Tabs */}
            <div className="flex h-full gap-px">
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => onTabChange(t.key)}
                        className={`px-[18px] h-full flex items-center gap-[7px] text-[12.5px] font-semibold border-b-[2.5px] transition-all bg-transparent border-none cursor-pointer ${
                            topTab === t.key
                                ? "text-[#209DB3] border-b-[#209DB3]"
                                : "text-[#94A3B8] border-b-transparent hover:text-[#0F172A] hover:bg-[#F8FAFC]"
                        }`}
                    >
                        <t.icon className="size-[14px] opacity-50" />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Right controls */}
            <div className="flex items-center gap-2">
                {/* Fullscreen toggle */}
                <button
                    onClick={() => setContentFullscreen(!contentFullscreen)}
                    title={contentFullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
                    className="w-[30px] h-[30px] rounded-md border border-[#E2E8F0] bg-white text-[#94A3B8] hover:border-[#209DB3] hover:text-[#209DB3] flex items-center justify-center transition-all cursor-pointer"
                >
                    {contentFullscreen ? <MinimizeIcon className="size-[13px]" /> : <MaximizeIcon className="size-[13px]" />}
                </button>

                {/* Settings dropdown with Xero tenant */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            title="Settings"
                            className="w-[30px] h-[30px] rounded-md border border-[#E2E8F0] bg-white text-[#94A3B8] hover:border-[#209DB3] hover:text-[#209DB3] flex items-center justify-center transition-all cursor-pointer"
                        >
                            <SettingsIcon className="size-[14px]" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[200px]">
                        <DropdownMenuLabel>Configuration</DropdownMenuLabel>
                        {xeroIntegration && xeroIntegration.xeroTenants?.length > 0 && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>
                                    <div className="flex flex-col gap-1.5">
                                        <span className="text-xs text-muted-foreground">Xero Tenant</span>
                                        <Select
                                            value={xeroIntegration.xeroTenantSelected?.tenantId || xeroIntegration.xeroTenantDefault?.tenantId}
                                            onValueChange={(tenantId) => void setXeroTenant(tenantId)}
                                        >
                                            <SelectTrigger className="min-w-[150px]">
                                                <SelectValue placeholder={xeroIntegration.xeroTenantSelected?.tenantName || xeroIntegration.xeroTenantDefault?.tenantName} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {xeroIntegration.xeroTenants.map((tenant: {tenantId: string; tenantName: string}) => (
                                                    <SelectItem key={tenant.tenantId} value={tenant.tenantId}>
                                                        {tenant.tenantName}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </DropdownMenuLabel>
                            </>
                        )}
                        <DropdownMenuSeparator />
                        <a href="/settings">
                            <DropdownMenuItem>
                                <SettingsIcon className="size-4" />
                                Settings
                            </DropdownMenuItem>
                        </a>
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Avatar dropdown */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button className="cursor-pointer">
                            <Avatar className="h-[30px] w-[30px] rounded-[7px] bg-gradient-to-br from-[#209DB3] to-[#1A8C9E]">
                                <AvatarFallback className="bg-transparent text-white text-[10px] font-bold">
                                    {avatarName}
                                </AvatarFallback>
                            </Avatar>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[200px]">
                        <DropdownMenuLabel className="p-0 font-normal">
                            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                <Avatar className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#209DB3] to-[#1A8C9E]">
                                    <AvatarFallback className="rounded-lg bg-transparent text-white font-medium">
                                        {avatarName}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-medium">{displayName}</span>
                                    <span className="truncate text-xs text-[#209DB3]">{user?.email}</span>
                                </div>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            <a href="/account">
                                <DropdownMenuItem>
                                    <UserCircleIcon className="size-4" />
                                    Account
                                </DropdownMenuItem>
                            </a>
                            <DropdownMenuItem>
                                <CreditCardIcon className="size-4" />
                                Billing
                            </DropdownMenuItem>
                            <a href="/settings">
                                <DropdownMenuItem>
                                    <SettingsIcon className="size-4" />
                                    Settings
                                </DropdownMenuItem>
                            </a>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                        <a href="/logout">
                            <DropdownMenuItem>
                                <LogOutIcon className="size-4" />
                                Log out
                            </DropdownMenuItem>
                        </a>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}
